/* ══════════════════════════════════════════════════════════════════════════
 * Attribute constraints — linked configurator fields.
 *
 * A variation's 3D settings may carry `three_d.constraints`:
 *   [{ attribute_ids: [12, 13, …] }, …]
 * Every group links attributes of ONE type (textfield, select or dropdown) so
 * they always hold the same choice: "Bogstav tryk" and "Bogstav broderi" show
 * and hide with the letter type, and the letter picked in one is the letter of
 * the other. Groups may link more than two attributes.
 *
 * Two attributes never share value ids, so choices are matched by their LABEL
 * (trimmed, case-insensitive); textfields share the typed text. A member that
 * has no option with the group's label holds NO selection and stays empty — it
 * is never filled with a default (see unavailableLinked) — until the customer
 * picks one of its real options, which then moves the whole group.
 *
 * Everything here is pure; hidden members are synced like visible ones (their
 * value is ready when they appear) — hiding already keeps them out of price,
 * cart and 3D commands.
 * ══════════════════════════════════════════════════════════════════════════ */

const LINKABLE_TYPES = new Set(['textfield', 'select', 'dropdown']);

const norm = (label) => String(label ?? '').trim().toLowerCase();

const isEmpty = (attr, value) => (attr.type === 'textfield'
  ? !(typeof value === 'string' && value !== '')
  : value == null);

/** Label of an attribute's current value: the typed text, or the chosen option's label. */
export function labelOf(attr, value) {
  if (!attr || isEmpty(attr, value)) return null;
  if (attr.type === 'textfield') return String(value);
  const option = (attr.values || []).find((v) => String(v.id) === String(value));
  return option ? String(option.value ?? '') : null;
}

/** The value an attribute holds for a label: the text itself, or the id of the option
 *  carrying that label; undefined when the attribute has no such option. */
export function valueForLabel(attr, label) {
  if (attr.type === 'textfield') return String(label);
  const wanted = norm(label);
  const option = (attr.values || []).find((v) => norm(v.value) === wanted);
  return option ? option.id : undefined;
}

/**
 * Constraint groups resolved against the page's attributes: arrays of attribute
 * objects. Members missing from the payload or of another type than the group's
 * first member are dropped; a group needs two members to mean anything, and an
 * attribute belongs to one group only (the first that names it).
 */
export function resolveConstraintGroups(threeD, attributes) {
  const byId = new Map((attributes || []).map((a) => [String(a.id), a]));
  const taken = new Set();
  const groups = [];
  for (const constraint of (Array.isArray(threeD?.constraints) ? threeD.constraints : [])) {
    const members = [];
    for (const id of (Array.isArray(constraint?.attribute_ids) ? constraint.attribute_ids : [])) {
      const attr = byId.get(String(id));
      if (!attr || !LINKABLE_TYPES.has(attr.type) || taken.has(attr.slug)) continue;
      if (members.length && attr.type !== members[0].type) continue;
      if (members.includes(attr)) continue;
      members.push(attr);
    }
    if (members.length < 2) continue;
    members.forEach((a) => taken.add(a.slug));
    groups.push(members);
  }
  return groups;
}

/** New selections after `slug` changed to `value`: the change itself plus its linked members. */
export function propagateConstraint(selected, slug, value, groups) {
  const next = { ...selected, [slug]: value };
  const group = (groups || []).find((members) => members.some((a) => a.slug === slug));
  if (!group) return next;
  const source = group.find((a) => a.slug === slug);
  const label = labelOf(source, value);
  for (const member of group) {
    if (member === source) continue;
    if (label == null) {
      // Cleared: the whole group is cleared (text fields keep the empty string the field typed).
      next[member.slug] = member.type === 'textfield' ? '' : null;
      continue;
    }
    const match = valueForLabel(member, label);
    next[member.slug] = match === undefined ? null : match;
  }
  return next;
}

/** The group's current label: the first member holding a value decides (visible members first). */
function groupLabel(group, selected, hiddenSlugs) {
  const ordered = [...group].sort((a, b) => Number(hiddenSlugs.has(a.slug)) - Number(hiddenSlugs.has(b.slug)));
  for (const member of ordered) {
    const label = labelOf(member, selected[member.slug]);
    if (label != null) return label;
  }
  return null;
}

/**
 * Fill members that hold NO value from the group's label (defaults, a restored
 * cart/order line — those carry the visible member only). Members that already
 * hold a value are never rewritten here: live changes go through
 * propagateConstraint. Returns `selected` itself when nothing changed.
 */
export function reconcileConstraints(selected, groups, hiddenSlugs = new Set()) {
  let next = selected;
  for (const group of (groups || [])) {
    const label = groupLabel(group, selected, hiddenSlugs);
    if (label == null) continue;
    for (const member of group) {
      if (!isEmpty(member, selected[member.slug])) continue;
      const match = valueForLabel(member, label);
      if (match === undefined) continue;
      if (next === selected) next = { ...selected };
      next[member.slug] = match;
    }
  }
  return next;
}

/**
 * { [slug]: label } for members that cannot hold the group's choice: they have
 * no value and no option with the group's label. The control stays EMPTY
 * (placeholder, required error if unanswered) and must not preselect a default
 * over it — that would silently change the linked field the customer did choose.
 */
export function unavailableLinked(selected, groups, hiddenSlugs = new Set()) {
  const out = {};
  for (const group of (groups || [])) {
    const label = groupLabel(group, selected, hiddenSlugs);
    if (label == null) continue;
    for (const member of group) {
      if (member.type === 'textfield') continue;
      if (!isEmpty(member, selected[member.slug])) continue;
      if (valueForLabel(member, label) === undefined) out[member.slug] = label;
    }
  }
  return out;
}
